import React, {useEffect, useMemo, useRef, useState} from "react";
import {SCENE, WithScene} from "./interfaces/UX";
import {DomOverlay} from "./components/dom-overlay/DomOverlay";
import {CustomARCanvas, WithInitialize, XRSessionInitWithDomOverlay} from "./components/ar-canvas/CustomARCanvas";
import {WithErrorHandling} from "./interfaces/ErrorHandling";
import {Vertex, WithFloorCreator} from "./interfaces/Floor";
import {DOFloorMarker} from "./scenes/ar/floor-marker/DOFloorMarker";
import {ARFloorMarker} from "./scenes/ar/floor-marker/ARFloorMarker";
import * as THREE from "three";
import {DORoomManager} from "./scenes/ar/room-manager/DORoomManager";
import {ARRoomManager} from "./scenes/ar/room-manager/ARRoomManager";
import {Room} from "./interfaces/Room";
import {DOMainMenu} from "./scenes/ar/main-menu/DOMainMenu";

/* Mutable objects to hold current hit location without re-render */
const hittingPosition = new THREE.Vector3();

export const ARVisualization = (props: JSX.IntrinsicElements['group'] & WithScene & WithErrorHandling & WithFloorCreator & WithInitialize) => {

    const domOverlayRef = useRef<HTMLDivElement>(null);
    const [overlay, setOverlay] = useState<Element | null>(null);
    const [markedVertices, setMarketVertices] = useState<Vertex[]>([]);
    const [rooms, setRooms] = useState<Room[]>([]);
    const {scene, changeScene, onError, initialize} = props;


    const sessionInit: XRSessionInitWithDomOverlay = {
        requiredFeatures: ["local-floor", "hit-test"],
    };

    useEffect(() => {
        if (domOverlayRef.current) {
            setOverlay(domOverlayRef.current);
        }

        return () => {
            setOverlay(null);
        };
    }, [domOverlayRef]);

    const arElement = useMemo(() => {
        switch (scene) {
            case SCENE.FLOOR_MARKER:
                console.log("Creating ARFloorMarker");
                return (
                    <ARFloorMarker
                        onLocationChanged={(loc) => hittingPosition.setFromMatrixPosition(loc)}
                        vertices={markedVertices}
                    />
                );
            case SCENE.ROOM_MANAGER:
                return (
                    <ARRoomManager rooms={rooms} />
                );
            default:
                return null;
        }
    }, [scene, markedVertices, rooms]);

    const doElement = useMemo(() => {
        switch (scene) {
            case SCENE.MAIN:
                return (<DOMainMenu scene={scene} changeScene={changeScene} />);
            case SCENE.FLOOR_MARKER:
                const markCorner = () => {
                    const {x, y, z} = hittingPosition;
                    const newMarkedVertices: Vertex[] = [...markedVertices, {x, y, z}];
                    if (newMarkedVertices.length > 1) {
                        const {x: x1, y: y1, z: z1} = newMarkedVertices[0];
                        const {x: x2, y: y2, z: z2} = newMarkedVertices[newMarkedVertices.length - 1];
                        if (x1 === x2 && y1 === y2 && z1 === z2) {
                            const newRoom: Room = {
                                vertices: newMarkedVertices,
                                area: Math.abs(THREE.ShapeUtils.area(newMarkedVertices.map(({x, z}) => ({x, y: z})))),
                            };
                            setRooms([...rooms, newRoom]);
                            setMarketVertices([]);
                            changeScene(SCENE.ROOM_MANAGER);
                        } else {
                            setMarketVertices(newMarkedVertices);
                        }
                    } else {
                        setMarketVertices(newMarkedVertices);
                    }
                };
                const undoCorner = () => {
                    setMarketVertices(markedVertices.slice(0, markedVertices.length - 1))
                };

                return (
                    <DOFloorMarker scene={scene} changeScene={changeScene} markCorner={markCorner} undoCorner={undoCorner} undoVisible={markedVertices.length > 0} />
                );
            case SCENE.ROOM_MANAGER:
                return (
                    <DORoomManager scene={scene} changeScene={changeScene} />
                );
            default:
                return null;
        }
    }, [scene, changeScene, markedVertices, rooms]);

    return (
        <>
            {overlay && (
                <CustomARCanvas domOverlay={overlay} onError={onError} onStarted={() => changeScene(SCENE.MAIN)}
                                onEnded={() => changeScene(SCENE.INIT)} sessionInit={sessionInit}
                                initialize={initialize}>
                    {arElement}
                </CustomARCanvas>
            )}

            <DomOverlay innerRef={domOverlayRef} visible={scene !== SCENE.INIT}>
                {doElement}
            </DomOverlay>
        </>
    );
};
